import { ref, effectScope, onUnmounted } from 'vue'

export function useWebSocket(
  url: string,
  options?: {
    messages?: string
    max?: number
    onMessage?: (data: string) => void
    handleClose?: (reconnect: () => void) => void
    handleOpen?: () => void
    handleError?: (reconnect: () => void) => void
  },
) {
  const scope = effectScope()
  const status = ref<'OPEN' | 'CLOSED' | 'ERROR'>('CLOSED')
  let ws: WebSocket | null = null
  let retry = 0
  const MAX_RETRY = 100
  let stopped = false
  let initMsg: string | null = null

  const reconnect = () => {
    if (stopped) {
      return
    }
    if (ws && ws.readyState !== WebSocket.CLOSED && ws.readyState !== WebSocket.CLOSING) {
      return
    }
    status.value = 'ERROR'
    if (retry++ < MAX_RETRY) {
      window.setTimeout(init, 1000 * retry)
    } else {
      console.warn('Max WS retries reached')
    }
  }
  const init = () => {
    ws = new WebSocket(url)
    ws.addEventListener('open', () => {
      status.value = 'OPEN'
      if (options?.messages) {
        ws!.send(options?.messages)
      }
      if (initMsg) {
        ws!.send(initMsg)
      }
      if (options?.handleOpen) {
        options.handleOpen()
      }
    })
    ws.addEventListener('message', (e) => {
      const { data } = e
      options?.onMessage?.(data)
    })
    ws.addEventListener('close', () => {
      status.value = 'CLOSED'
      if (options?.handleClose) {
        options.handleClose(reconnect)
      }
    })
    ws.addEventListener('error', () => {
      if (options?.handleError) {
        options.handleError(reconnect)
      }
    })
  }
  const run = (runMsg: string) => {
    initMsg = runMsg
    stopped = false
    if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
      scope.run(() => init())
    } else if (ws.readyState === WebSocket.OPEN) {
      ws.send(runMsg)
    }
  }
  const stopForever = () => {
    stopped = true
    ws?.close()
    scope.stop()
  }
  const stop = () => {
    stopped = true
    ws?.close()
  }
  const send = (data: string) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(data)
    }
  }

  onUnmounted(stopForever)
  return { run, stop, send, status }
}
